import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

/*
 * cart-gift-picker.js
 * -------------------
 * Подаръците в количката: избор на саше от клиента + автоматичните нива.
 *
 * Всичко се смята по РЕАЛНО ПЛАТЕНАТА сума (cart.total_price). Точно тук е
 * разликата с Hitsy, който смяташе по сумата преди отстъпките и раздаваше
 * наградите по-рано, отколкото трябва.
 *
 * Правила:
 *   · сашета по избор — 1 при всяка поръчка, 2 над SACHET_THRESHOLD
 *   · нивата с автоматични подаръци идват от <cart-progress-bar data-tiers>
 *
 * Всяка промяна е ЕДНА заявка към /cart/update.js, която връща и HTML-а на
 * секциите — подаваме го на morphSection на темата.
 */

const SACHETS = ['57227310170492', '57291009655164', '57291009687932', '57291009720700', '57330584879484'];
// Автоматичните подаръци по нива — същите варианти като в snippets/cart-progress-bar.liquid.
// Нужни са и тук, защото лентата не се рендира, когато в количката са останали само подаръци.
const AUTO_GIFTS = ['57325106364796', '57325104398716', '57325102367100', '57326230864252'];
const SACHET_THRESHOLD = 4999;

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
let busy = false;
let noticeTimer = null;

/** €12,34 от стотинки */
function money(cents) {
  return '€' + (cents / 100).toFixed(2).replace('.', ',');
}

/**
 * Кратко съобщение защо подарък е отпаднал. Без него изчезването изглежда
 * като бъг точно в момента, в който клиентът въвежда промо код.
 */
function notice(text) {
  let element = document.querySelector('.cgp-notice');
  if (!element) {
    element = document.createElement('div');
    element.className = 'cgp-notice';
    element.setAttribute('role', 'status');
    document.body.appendChild(element);
  }
  element.textContent = text;
  element.classList.add('is-on');
  clearTimeout(noticeTimer);
  noticeTimer = setTimeout(() => element.classList.remove('is-on'), 5000);
}

/** Изпраща промените и пре-рендира секциите по пътя на темата. */
async function applyUpdates(updates) {
  const components = [...document.querySelectorAll('cart-items-component')];
  const sectionIds = components.map((component) => component.dataset.sectionId).filter(Boolean);

  const response = await fetch(`${root()}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates, sections: sectionIds.join(','), sections_url: window.location.pathname }),
  });

  const cart = await response.json();
  const sections = cart.sections || {};

  components[0]?.dispatchEvent(
    new CartUpdateEvent(cart, 'cart-gift-picker', {
      itemCount: cart.item_count,
      source: 'cart-gift-picker',
      sections,
    })
  );

  await Promise.all(sectionIds.map((id) => (sections[id] ? morphSection(id, sections[id]) : Promise.resolve())));
  return cart;
}

/**
 * Изравнява количката с правилата: маха излишните сашета и слага/маха
 * автоматичните подаръци според платената сума. Праща заявка само при разлика.
 */
async function enforceRules() {
  if (busy) return;

  const cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
  if (!cart.items?.length) return;

  const qtyOf = (id) => cart.items.find((item) => String(item.variant_id) === String(id))?.quantity || 0;
  const updates = {};

  // Останали са само подаръци (клиентът е махнал всичките си продукти) →
  // количката трябва да може да стане празна, иначе подаръкът виси вечно.
  const isGift = (id) => SACHETS.includes(String(id)) || AUTO_GIFTS.includes(String(id));
  const hasRealItems = cart.items.some((item) => !isGift(item.variant_id));

  if (!hasRealItems) {
    for (const item of cart.items) updates[String(item.variant_id)] = 0;
    busy = true;
    try {
      await applyUpdates(updates);
    } catch (error) {
      console.error('[cart-gift-rules]', error);
    } finally {
      setTimeout(() => {
        busy = false;
      }, 400);
    }
    return;
  }

  // 1) сашета по избор — до 1 или 2 според платената сума
  const allowed = cart.total_price >= SACHET_THRESHOLD ? 2 : 1;
  let kept = 0;
  for (const item of cart.items) {
    const id = String(item.variant_id);
    if (!SACHETS.includes(id)) continue;
    const keep = Math.max(0, Math.min(item.quantity, allowed - kept));
    kept += keep;
    if (keep !== item.quantity) updates[id] = keep;
  }

  // 2) автоматични нива, дефинирани в лентата
  const bar = document.querySelector('cart-progress-bar');
  const tiers = (bar?.dataset.tiers || '')
    .split('|')
    .filter(Boolean)
    .map((entry) => {
      const [threshold, variants] = entry.split(':');
      return { threshold: Number(threshold), variants: variants.split(',').filter(Boolean) };
    });

  let droppedTier = null;
  for (const tier of tiers) {
    const shouldHave = cart.total_price >= tier.threshold ? 1 : 0;
    for (const variant of tier.variants) {
      if (qtyOf(variant) === shouldHave) continue;
      updates[variant] = shouldHave;
      if (shouldHave === 0) droppedTier = tier.threshold;
    }
  }

  const droppedSachet = kept < cart.items
    .filter((item) => SACHETS.includes(String(item.variant_id)))
    .reduce((sum, item) => sum + item.quantity, 0);

  if (!Object.keys(updates).length) return;

  busy = true;
  try {
    await applyUpdates(updates);
    if (droppedTier) {
      notice('Подаръкът отпадна — сумата в количката е под ' + money(droppedTier) + '.');
    } else if (droppedSachet) {
      notice('Второто саше отпадна — сумата е под ' + money(SACHET_THRESHOLD) + '.');
    }
  } catch (error) {
    console.error('[cart-gift-rules]', error);
  } finally {
    setTimeout(() => {
      busy = false;
    }, 400);
  }
}

class CartGiftPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const card = event.target.closest('.cgp__card');
    if (!card) return;

    const variantId = card.dataset.variant;
    const quantity = Number(card.dataset.qty || 0);
    const state = card.dataset.state;

    if (state === 'picked') {
      this.#paintNow(card, false);
      this.#run({ [variantId]: quantity - 1 }, card);
      return;
    }

    if (state === 'full') {
      const current = this.querySelector('.cgp__card[data-state="picked"]');
      const updates = { [variantId]: quantity + 1 };
      if (current) updates[current.dataset.variant] = Number(current.dataset.qty || 1) - 1;
      this.#paintNow(card, true);
      this.#run(updates, card);
      return;
    }

    this.#paintNow(card, true);
    this.#run({ [variantId]: quantity + 1 }, card);
  };

  /** Рисуваме избора веднага — заявката към Shopify отнема около секунда. */
  #paintNow(card, picking) {
    const setFlag = (element, text) => {
      const flag = element.querySelector('.cgp__flag');
      if (flag) flag.textContent = text;
    };

    if (picking) {
      const previous = this.querySelector('.cgp__card[data-state="picked"]');
      if (previous && previous !== card && card.dataset.state === 'full') {
        previous.dataset.state = 'full';
        previous.querySelector('.cgp__tick')?.remove();
        setFlag(previous, 'СМЕНИ С ТОВА');
      }
      card.dataset.state = 'picked';
      setFlag(card, 'ИЗБРАНО');
      if (!card.querySelector('.cgp__tick')) {
        const tick = document.createElement('span');
        tick.className = 'cgp__tick';
        tick.setAttribute('aria-hidden', 'true');
        tick.textContent = '✓';
        card.prepend(tick);
      }
    } else {
      card.dataset.state = 'free';
      card.querySelector('.cgp__tick')?.remove();
      setFlag(card, 'БЕЗПЛАТНО');
    }
  }

  async #run(updates, card) {
    if (busy || this.classList.contains('is-busy')) return;
    busy = true;
    this.classList.add('is-busy');
    card?.classList.add('cgp__card--pending');

    try {
      await applyUpdates(updates);
    } catch (error) {
      console.error('[cart-gift-picker]', error);
      this.classList.remove('is-busy');
      card?.classList.remove('cgp__card--pending');
    } finally {
      busy = false;
      enforceRules();
    }
  }
}

if (!customElements.get('cart-gift-picker')) {
  customElements.define('cart-gift-picker', CartGiftPicker);
}

document.addEventListener('DOMContentLoaded', enforceRules);
document.addEventListener('cart:update', (event) => {
  if (event?.detail?.data?.source === 'cart-gift-picker') return;
  enforceRules();
});

/*
 * Линкът „смени подаръка" стои в самия ред на количката, извън елемента, затова
 * се хваща на ниво документ: превърта до избора и подсветва подаръчния ред.
 */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-cgp-scroll]');
  if (!trigger) return;

  const picker = document.querySelector('cart-gift-picker');
  if (!picker) return;

  // Без 'smooth': в scroll-hint контейнера на drawer-а плавното превъртане
  // не помръдва скрола изобщо (проверено), затова скачаме директно.
  picker.scrollIntoView({ block: 'center' });

  const row = trigger.closest('.cart-items__table-row--gift');
  if (!row) return;
  row.classList.add('cgp-flash');
  setTimeout(() => row.classList.remove('cgp-flash'), 900);
});

/* Стилът на съобщението живее тук, за да няма зависимост от темата. */
const noticeStyles = document.createElement('style');
noticeStyles.textContent = `
  .cgp-notice {
    position: fixed;
    left: 50%;
    bottom: 22px;
    transform: translate(-50%, 12px);
    background: #12140f;
    color: #fff;
    padding: 12px 18px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
    max-width: min(90vw, 420px);
    text-align: center;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s, transform 0.25s;
    z-index: 2147483000;
  }
  .cgp-notice.is-on {
    opacity: 1;
    transform: translate(-50%, 0);
  }
`;
document.head.appendChild(noticeStyles);
